import { useState, useEffect, useRef } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import {
  Edit, Trash2, Share2, ExternalLink, Send, Paperclip, Download, X,
  ArrowLeft, Copy, MessageSquare, Video, Image, Eye
} from 'lucide-react'
import { supportTaskApi } from '../services/api'
import { showError } from '../utils/showError'
import { getApiBaseUrl } from '../utils/config'
import RichTextEditor from '../components/RichTextEditor'
import RichTextContent from '../components/RichTextContent'
import {
  PageHeader,
  LoadingSpinner,
  Button,
  Card,
  CardHeader,
  CardBody,
} from '../components/common'

const statusLabels = { 1: 'Open', 2: 'In Progress', 3: 'On Hold', 4: 'Resolved', 5: 'Closed' }
const statusColors = { 1: 'info', 2: 'warning', 3: 'default', 4: 'success', 5: 'default' }
const priorityLabels = { 1: 'Low', 2: 'Medium', 3: 'High', 4: 'Critical' }
const priorityColors = { 1: 'default', 2: 'warning', 3: 'danger', 4: 'danger' }
const typeLabels = { 1: 'Bug', 2: 'Feature', 3: 'Improvement', 4: 'Maintenance', 5: 'Investigation', 6: 'Other' }

const scopeFlags = (scope) => {
  const parts = []
  if (scope & 1) parts.push('API')
  if (scope & 2) parts.push('Mobile')
  if (scope & 4) parts.push('Portal')
  return parts.join(', ') || '-'
}

const formatDate = (dateString) => {
  if (!dateString) return '-'
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'
  })
}

const formatFileSize = (bytes) => {
  if (!bytes) return '0 B'
  const k = 1024
  const sizes = ['B', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i]
}

const getAttachmentIcon = (contentType) => {
  if (!contentType) return Paperclip
  if (contentType.startsWith('video/')) return Video
  if (contentType.startsWith('image/')) return Image
  return Paperclip
}

const TaskDetail = () => {
  const { supportTaskId } = useParams()
  const navigate = useNavigate()
  const [task, setTask] = useState(null)
  const [loading, setLoading] = useState(true)
  const [commentText, setCommentText] = useState('')
  const [submittingComment, setSubmittingComment] = useState(false)
  const [uploadingFile, setUploadingFile] = useState(false)
  const [copied, setCopied] = useState(false)
  const fileInputRef = useRef(null)
  const [assigneeOptions, setAssigneeOptions] = useState([])

  useEffect(() => {
    loadTask()
    loadAssignees()
  }, [supportTaskId])

  // Page-level paste handler for image attachments
  useEffect(() => {
    const handlePaste = async (e) => {
      if (e.target.closest('.ProseMirror')) return
      const items = e.clipboardData?.items
      if (!items) return
      for (const item of items) {
        if (item.type.startsWith('image/')) {
          e.preventDefault()
          const file = item.getAsFile()
          if (!file) continue
          const formData = new FormData()
          formData.append('file', file)
          setUploadingFile(true)
          try {
            await supportTaskApi.uploadAttachment(supportTaskId, formData)
            await loadTask()
          } catch (err) {
            console.error('Error uploading pasted image:', err)
            showError(err)
          } finally {
            setUploadingFile(false)
          }
          break
        }
      }
    }
    document.addEventListener('paste', handlePaste)
    return () => document.removeEventListener('paste', handlePaste)
  }, [supportTaskId])

  const loadTask = async () => {
    try {
      setLoading(true)
      const response = await supportTaskApi.get(supportTaskId)
      setTask(response.data)
    } catch (error) {
      console.error('Error loading task:', error)
    } finally {
      setLoading(false)
    }
  }

  const loadAssignees = async () => {
    try {
      const response = await supportTaskApi.getMembers()
      setAssigneeOptions(response.data || [])
    } catch (error) {
      console.error('Error loading task members:', error)
    }
  }

  const handleStatusChange = async (newStatus) => {
    try {
      await supportTaskApi.updateStatus(supportTaskId, newStatus)
      await loadTask()
    } catch (error) {
      console.error('Error updating status:', error)
      showError(error)
    }
  }

  const handleFieldChange = async (field, value) => {
    try {
      if (field === 'status') {
        await supportTaskApi.updateStatus(supportTaskId, parseInt(value))
      } else if (field === 'assignedToUserId') {
        await supportTaskApi.update(supportTaskId, { [field]: value })
      } else {
        await supportTaskApi.update(supportTaskId, { [field]: parseInt(value) })
      }
      await loadTask()
    } catch (error) {
      console.error(`Error updating ${field}:`, error)
      showError(error)
    }
  }

  const handleAssigneeChange = async (value) => {
    try {
      if (value) {
        await supportTaskApi.update(supportTaskId, { assignedToTaskMemberId: parseInt(value) })
      } else {
        await supportTaskApi.update(supportTaskId, { clearAssignedTaskMember: true })
      }
      await loadTask()
    } catch (error) {
      console.error('Error updating assignee:', error)
      showError(error)
    }
  }

  const handleCommentImageUpload = async (file) => {
    const fd = new FormData()
    fd.append('file', file)
    const res = await supportTaskApi.uploadTempImage(fd)
    return res.data.url
  }

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this task? This action cannot be undone.')) return
    try {
      await supportTaskApi.delete(supportTaskId)
      navigate('/tasks')
    } catch (error) {
      console.error('Error deleting task:', error)
      showError(error)
    }
  }

  const isCommentEmpty = (html) => {
    if (!html) return true
    const text = html.replace(/<[^>]*>/g, '').trim()
    return text.length === 0
  }

  const handleAddComment = async (e) => {
    e?.preventDefault()
    if (isCommentEmpty(commentText)) return
    try {
      setSubmittingComment(true)
      await supportTaskApi.addComment(supportTaskId, { content: commentText })
      setCommentText('')
      await loadTask()
    } catch (error) {
      console.error('Error adding comment:', error)
      showError(error)
    } finally {
      setSubmittingComment(false)
    }
  }

  const handleDeleteComment = async (commentId) => {
    if (!window.confirm('Delete this comment?')) return
    try {
      await supportTaskApi.deleteComment(commentId)
      await loadTask()
    } catch (error) {
      console.error('Error deleting comment:', error)
      showError(error)
    }
  }

  const handleUploadAttachment = async (e) => {
    const file = e.target.files?.[0]
    if (!file) return
    try {
      setUploadingFile(true)
      const formData = new FormData()
      formData.append('file', file)
      await supportTaskApi.uploadAttachment(supportTaskId, formData)
      await loadTask()
    } catch (error) {
      console.error('Error uploading attachment:', error)
      showError(error)
    } finally {
      setUploadingFile(false)
      if (fileInputRef.current) fileInputRef.current.value = ''
    }
  }

  const handleDeleteAttachment = async (attachmentId) => {
    if (!window.confirm('Delete this attachment?')) return
    try {
      await supportTaskApi.deleteAttachment(attachmentId)
      await loadTask()
    } catch (error) {
      console.error('Error deleting attachment:', error)
      showError(error)
    }
  }

  const handleGeneratePublicLink = async () => {
    try {
      const response = await supportTaskApi.generatePublicLink(supportTaskId)
      await loadTask()
      const publicUrl = `${getApiBaseUrl()}/api/public/tasks/${response.data.publicToken}`
      await navigator.clipboard.writeText(publicUrl)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch (error) {
      console.error('Error generating public link:', error)
      showError(error)
    }
  }

  const handleCopyPublicLink = async () => {
    const publicUrl = `${getApiBaseUrl()}/api/public/tasks/${task.publicToken}`
    await navigator.clipboard.writeText(publicUrl)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  if (loading) return <LoadingSpinner />
  if (!task) return <div className="text-center py-12 text-gray-500">Task not found</div>

  return (
    <div className="space-y-6">
      <PageHeader
        title={
          <div className="flex items-center space-x-3">
            <span className="text-sm font-mono text-gray-500">{task.taskNumber}</span>
            <span>{task.title}</span>
          </div>
        }
        action={
          <div className="flex items-center space-x-3">
            <Button variant="secondary" onClick={() => navigate('/tasks')}>
              <ArrowLeft className="mr-2 h-4 w-4" />Back
            </Button>
            <Button variant="secondary" onClick={() => navigate(`/tasks/${supportTaskId}/edit`)}>
              <Edit className="mr-2 h-4 w-4" />Edit
            </Button>
            <Button variant="danger" onClick={handleDelete}>
              <Trash2 className="mr-2 h-4 w-4" />Delete
            </Button>
          </div>
        }
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Details */}
        <div className="lg:col-span-2 space-y-6">
          {/* Description */}
          {task.description && (
            <Card>
              <CardHeader title="Description" />
              <CardBody>
                <RichTextContent content={task.description} className="text-sm text-gray-700" />
              </CardBody>
            </Card>
          )}

          {/* Comments */}
          <Card>
            <CardHeader
              title={`Comments (${task.comments?.length || 0})`}
            />
            <CardBody>
              <div className="space-y-4">
                {task.comments?.map((comment) => (
                  <div key={comment.taskCommentId} className={`flex items-start space-x-3 ${comment.isSystemGenerated ? 'opacity-70' : ''}`}>
                    <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center text-xs font-medium text-gray-600 flex-shrink-0">
                      {comment.isSystemGenerated ? <MessageSquare size={14} /> : (comment.createdByName?.[0] || '?')}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center space-x-2">
                        <span className="text-sm font-medium text-gray-900">
                          {comment.isSystemGenerated ? 'System' : (comment.createdByName || 'Unknown')}
                        </span>
                        <span className="text-xs text-gray-500">{formatDate(comment.createDateTimeUtc)}</span>
                      </div>
                      {comment.isSystemGenerated ? (
                        <p className="text-sm mt-1 text-gray-500 italic">{comment.content}</p>
                      ) : (
                        <RichTextContent content={comment.content} className="text-sm mt-1 text-gray-700" />
                      )}
                    </div>
                    {!comment.isSystemGenerated && (
                      <button onClick={() => handleDeleteComment(comment.taskCommentId)} className="text-gray-400 hover:text-red-500 transition-colors">
                        <X size={14} />
                      </button>
                    )}
                  </div>
                ))}

                {(!task.comments || task.comments.length === 0) && (
                  <p className="text-sm text-gray-500 text-center py-4">No comments yet</p>
                )}

                {/* Add comment form */}
                <div className="pt-4 border-t border-gray-200 space-y-2">
                  <RichTextEditor
                    compact
                    content={commentText}
                    onChange={setCommentText}
                    onMediaUpload={handleCommentImageUpload}
                    placeholder="Add a comment..."
                    onSubmit={handleAddComment}
                  />
                  <div className="flex justify-end">
                    <Button variant="primary" size="small" loading={submittingComment} disabled={isCommentEmpty(commentText)} onClick={handleAddComment}>
                      <Send size={14} className="mr-1.5" /> Send
                    </Button>
                  </div>
                </div>
              </div>
            </CardBody>
          </Card>

          {/* Attachments */}
          <Card>
            <CardHeader
              title={`Attachments (${task.attachments?.length || 0})`}
              action={
                <div>
                  <input
                    ref={fileInputRef}
                    type="file"
                    onChange={handleUploadAttachment}
                    accept="image/*,video/*,.pdf,.doc,.docx,.xls,.xlsx,.zip,.rar,.txt"
                    className="hidden"
                  />
                  <Button
                    variant="secondary"
                    size="small"
                    loading={uploadingFile}
                    onClick={() => fileInputRef.current?.click()}
                  >
                    <Paperclip className="mr-1.5 h-3.5 w-3.5" />
                    Upload
                  </Button>
                </div>
              }
            />
            <CardBody>
              {task.attachments?.length > 0 ? (
                <div className="space-y-2">
                  {task.attachments.map((att) => {
                    const IconComponent = getAttachmentIcon(att.contentType)
                    return (
                      <div key={att.taskAttachmentId} className="space-y-2">
                        {att.contentType?.startsWith('video/') && att.downloadUrl && (
                          <video controls src={att.downloadUrl} className="rounded-lg max-w-full max-h-64" preload="metadata" />
                        )}
                        {att.contentType?.startsWith('image/') && att.downloadUrl && (
                          <img src={att.downloadUrl} alt={att.originalFileName} className="rounded-lg max-w-full max-h-64 object-contain" />
                        )}
                        <div className="flex items-center justify-between py-2 px-3 bg-gray-50 rounded-lg">
                          <div className="flex items-center space-x-3 min-w-0">
                            <IconComponent size={16} className="text-gray-400 flex-shrink-0" />
                            <div className="min-w-0">
                              <p className="text-sm font-medium text-gray-900 truncate">{att.originalFileName}</p>
                              <p className="text-xs text-gray-500">{formatFileSize(att.fileSize)} &middot; {att.uploadedByName || 'Unknown'}</p>
                            </div>
                          </div>
                          <div className="flex items-center space-x-2 flex-shrink-0">
                            {att.downloadUrl && (
                              <a href={att.downloadUrl} target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-gray-600 transition-colors" title="View">
                                <Eye size={16} />
                              </a>
                            )}
                            {att.downloadUrl && (
                              <a href={att.downloadUrl} download target="_blank" rel="noopener noreferrer" className="text-primary-600 hover:text-primary-700" title="Download">
                                <Download size={16} />
                              </a>
                            )}
                            <button onClick={() => handleDeleteAttachment(att.taskAttachmentId)} className="text-gray-400 hover:text-red-500 transition-colors" title="Delete">
                              <X size={16} />
                            </button>
                          </div>
                        </div>
                      </div>
                    )
                  })}
                </div>
              ) : (
                <p className="text-sm text-gray-500 text-center py-4">No attachments</p>
              )}
            </CardBody>
          </Card>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Task Info */}
          <Card>
            <CardHeader title="Details" />
            <CardBody>
              <dl className="space-y-4">
                <div>
                  <dt className="text-xs font-medium text-gray-500 uppercase">Status</dt>
                  <dd className="mt-1">
                    <select
                      value={task.status}
                      onChange={(e) => handleFieldChange('status', e.target.value)}
                      className="text-sm border border-gray-200 rounded-md px-2 py-1 w-full focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    >
                      {Object.entries(statusLabels).map(([val, label]) => (
                        <option key={val} value={val}>{label}</option>
                      ))}
                    </select>
                  </dd>
                </div>
                <div>
                  <dt className="text-xs font-medium text-gray-500 uppercase">Priority</dt>
                  <dd className="mt-1">
                    <select
                      value={task.priority}
                      onChange={(e) => handleFieldChange('priority', e.target.value)}
                      className="text-sm border border-gray-200 rounded-md px-2 py-1 w-full focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    >
                      {Object.entries(priorityLabels).map(([val, label]) => (
                        <option key={val} value={val}>{label}</option>
                      ))}
                    </select>
                  </dd>
                </div>
                <div>
                  <dt className="text-xs font-medium text-gray-500 uppercase">Type</dt>
                  <dd className="mt-1">
                    <select
                      value={task.taskType}
                      onChange={(e) => handleFieldChange('taskType', e.target.value)}
                      className="text-sm border border-gray-200 rounded-md px-2 py-1 w-full focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    >
                      {Object.entries(typeLabels).map(([val, label]) => (
                        <option key={val} value={val}>{label}</option>
                      ))}
                    </select>
                  </dd>
                </div>
                <div>
                  <dt className="text-xs font-medium text-gray-500 uppercase">Project Scope</dt>
                  <dd className="mt-1 text-sm text-gray-900">{scopeFlags(task.projectScope)}</dd>
                </div>
                <div>
                  <dt className="text-xs font-medium text-gray-500 uppercase">Assigned To</dt>
                  <dd className="mt-1">
                    <select
                      value={task.assignedToTaskMemberId || ''}
                      onChange={(e) => handleAssigneeChange(e.target.value)}
                      className="text-sm border border-gray-200 rounded-md px-2 py-1 w-full focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    >
                      <option value="">Unassigned</option>
                      {assigneeOptions.filter(m => m.isActive).map(m => (
                        <option key={m.taskMemberId} value={m.taskMemberId}>
                          {m.name}
                        </option>
                      ))}
                    </select>
                  </dd>
                </div>
                <div>
                  <dt className="text-xs font-medium text-gray-500 uppercase">Due Date</dt>
                  <dd className={`mt-1 text-sm ${task.isOverdue ? 'text-red-600 font-medium' : 'text-gray-900'}`}>
                    {task.dueDate ? formatDate(task.dueDate) : '-'}
                    {task.isOverdue && ' (Overdue)'}
                  </dd>
                </div>
                {task.resolvedAt && (
                  <div>
                    <dt className="text-xs font-medium text-gray-500 uppercase">Resolved At</dt>
                    <dd className="mt-1 text-sm text-gray-900">{formatDate(task.resolvedAt)}</dd>
                  </div>
                )}
                <div>
                  <dt className="text-xs font-medium text-gray-500 uppercase">Created By</dt>
                  <dd className="mt-1 text-sm text-gray-900">{task.createdByName || '-'}</dd>
                </div>
                <div>
                  <dt className="text-xs font-medium text-gray-500 uppercase">Created</dt>
                  <dd className="mt-1 text-sm text-gray-900">{formatDate(task.createDateTimeUtc)}</dd>
                </div>
                <div>
                  <dt className="text-xs font-medium text-gray-500 uppercase">Updated</dt>
                  <dd className="mt-1 text-sm text-gray-900">{formatDate(task.updateDateTimeUtc)}</dd>
                </div>
              </dl>
            </CardBody>
          </Card>

          {/* Sentry Link */}
          {task.sentryIssueUrl && (
            <Card>
              <CardHeader title="Sentry Issue" />
              <CardBody>
                <a
                  href={task.sentryIssueUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center text-sm text-primary-600 hover:text-primary-700"
                >
                  <ExternalLink className="mr-2 h-4 w-4" />
                  View in Sentry
                </a>
                {task.sentryIssueId && (
                  <p className="text-xs text-gray-500 mt-1">Issue ID: {task.sentryIssueId}</p>
                )}
              </CardBody>
            </Card>
          )}

          {/* Public Sharing */}
          <Card>
            <CardHeader title="Public Sharing" />
            <CardBody>
              {task.publicToken ? (
                <div className="space-y-3">
                  <p className="text-xs text-gray-500">This task has a public link.</p>
                  <div className="flex items-center space-x-2">
                    <input
                      readOnly
                      value={`${getApiBaseUrl()}/api/public/tasks/${task.publicToken}`}
                      className="flex-1 text-xs bg-gray-50 border border-gray-200 rounded px-2 py-1.5 text-gray-600"
                    />
                    <Button variant="secondary" size="small" onClick={handleCopyPublicLink}>
                      <Copy size={14} />
                    </Button>
                  </div>
                  {copied && <p className="text-xs text-green-600">Copied to clipboard!</p>}
                </div>
              ) : (
                <Button variant="secondary" size="small" onClick={handleGeneratePublicLink}>
                  <Share2 className="mr-1.5 h-3.5 w-3.5" />
                  Generate Public Link
                </Button>
              )}
            </CardBody>
          </Card>
        </div>
      </div>
    </div>
  )
}

export default TaskDetail
